<template>

  <l-table
    ref="LTable"
    :module-name="MODULE_NAME"
    :table-columns="tableColumns"
    :filter-class="filterClass"
  >
    <template #before-search>
      <b-dropdown
        v-if="isInno"
        variant="success"
        toggle-class="text-decoration-none"
        no-caret
        class="mr-2"
      >
        <template #button-content>
          <b-icon icon="file-earmark-excel" /> Excel
        </template>
        <download-file dispatch-action="staticModule/supervisorList">
          {{ $t('Download apply docs with supervisor') }}
        </download-file>
        <download-file dispatch-action="staticModule/applyDocsList">
          {{ $t('Download apply docs') }}
        </download-file>
      </b-dropdown>
    </template>
    <template #create-button>
      <b-button
        v-if="canCreate(!(isInno || isOtm))"
        v-b-toggle.sidebar-create-apply-document
        variant="primary"
      >
        {{ $t('Create') }}
      </b-button>

      <b-button
        v-if="canCreateOld(isOtm || isInno)"
        v-b-toggle.sidebar-old-create-apply-document
        variant="primary"
        @click="onCreate"
      >
        {{ $t('Create') }}
      </b-button>
    </template>
    <template #create-body>
      <old-rejected
        ref="rejectOldApplyDocument"
        @refetchData="refetchData"
      />
      <create
        ref="createApplyDocument"
        @refetchData="refetchData"
      />
      <create-old
        ref="createOldApplyDocument"
        @refetchData="refetchData"
      />
      <manage-apply-doc-inno
        v-if="isInno"
        ref="manageApplyDocInno"
        @refetchData="refetchData"
      />
    </template>
    <template #cell(dc_type)="{data}">
      {{ $langName(getDcType(data.value).name) }}
    </template>

    <template #cell(user)="{data}">
      {{ data.value.full_name }}
    </template>

    <template #cell(edu_lang)="{data}">
      {{ $langName(getEduLang(data.value).name) }}
    </template>

    <template #cell(quarter)="{data}">
      {{ data.value.period.admission_year + '/' +data.value.quarter }}
    </template>

    <template #cell(organization)="{data}">
      {{ $langName(data.value.name) }}
    </template>

    <template #cell(direction)="{data}">
      {{ data.value.code + ' - ' + $langName(data.value.name) }}
    </template>
    <template #cell(monitoring)="{data}">
      {{ $t('monitoring_1') }}: {{ getMonitoringName(data.item.monitoring_1) }}
      {{ $t('monitoring_2') }}: {{ getMonitoringName(data.item.monitoring_2) }}
      {{ $t('monitoring_3') }}: {{ getMonitoringName(data.item.monitoring_3) }}
    </template>
    <template #cell(certificate)="{data}">
      {{ getCertificateName(data.item) }}
    </template>
    <template #cell(applied_time)="{data}">
      {{ data.value ? new Intl.DateTimeFormat($i18n.locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(data.value)) : null }}
    </template>
    <!-- Column: Actions -->
    <template #cell(mob_phone_no)="{data}">
      {{ data.value.mob_phone_no }}
    </template>
    <template #cell(status)="{data}">
      <b-badge :variant="getApplyDocumentStatusColor(data.value)">
        {{ $langName(getApplyDocumentStatus(data.value).name) }}
      </b-badge>
      <div v-if="data.item.is_old">
        <div style="margin-top: 5px">
          <b-badge :variant="data.item.is_old_confirmed ? 'success' : 'danger'">
            {{ data.item.is_old_confirmed ? 'Tasdiqlangan' : 'Tasdiqlanmagan' }}
          </b-badge>
          <b-link
            v-if="data.item.old_apply_doc_file"
            :href="data.item.old_apply_doc_file"
            download="file"
            target="_blank"
            class="btn btn-primary"
            style="padding: 2px; margin-left: 5px"
          >
            <feather-icon
              icon="EyeIcon"
              class="cursor-pointer"
              size="16"
            />
          </b-link>
        </div>
      </div>
    </template>

    <!-- Column: Actions -->
    <template #cell(actions)="{data}">

      <div class="text-nowrap">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="$router.push({ name: (isOtm ? 'applicant-document-view' : isInno ? 'apply-document-all-view' : 'apply-document-view' ), params: { id: data.item.id }})"
        />
        <feather-icon
          v-if="canUpdate(data.item) || canUpdateByInno(data.item)"
          v-b-toggle.sidebar-create-apply-document
          icon="EditIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="update(data.item)"
        />
        <feather-icon
          v-if="canUpdateOld(data.item)"
          v-b-toggle.sidebar-old-create-apply-document
          icon="EditIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="updateOld(data.item)"
        />
        <feather-icon
          v-if="canDelete(data.item)"
          icon="TrashIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="remove(data.item)"
        />
        <feather-icon
          v-if="canDeleteOld(data.item)"
          icon="TrashIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="removeOld(data.item)"
        />
        <b-dropdown
          v-if="canConfirmOld(data.item)"
          variant="link"
          toggle-class="text-decoration-none p-0 text-dark"
          no-caret
        >
          <template #button-content>
            <feather-icon
              icon="CheckCircleIcon"
              class="cursor-pointer mr-1"
              size="16"
            />
          </template>
          <b-dropdown-item @click="confirmOld(data.item)">
            {{ $t('Confirm') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-b-toggle.sidebar-old-reject-apply-document
            @click="rejectedOld(data.item)"
          >
            {{ $t('Rejected') }}
          </b-dropdown-item>
        </b-dropdown>
        <feather-icon
          v-if="canChangeToDraft(data.item)"
          :id="`can-change-to-draf-${data.item.id}`"
          icon="UnlockIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="changeToDraft(data.item)"
        />
        <b-tooltip
          :title="$t('Change to Draft')"
          :target="`can-change-to-draf-${data.item.id}`"
        />
        <feather-icon
          v-if="isInno && $can('manage', 'All')"
          v-b-toggle.sidebar-manage-apply-document
          icon="Edit3Icon"
          class="cursor-pointer mr-1"
          size="16"
          @click="manageApplyDoc(data.item)"
        />
      </div>
    </template>
  </l-table>
</template>

<script>
import {
  BButton, VBToggle, BBadge, BDropdown, BIcon, BTooltip, BLink, BSpinner, BDropdownItem,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import config from './config'
import applyDocumentModule from '@/store/apply-document/index'
import periodModule from '@/store/period/index'
import Create from './Create.vue'
import permission from './permission'
import { getUserData, myOrganization } from '@/auth/utils'
import ManageApplyDocInno from './ManageApplyDocInno.vue'
import LTable from '@/views/components/LTable.vue'
import ApplyDocument from '@/views/components/filters/service/ApplyDocument'
import DownloadFile from '@/views/pages/apply-document/components/DownloadFile.vue'
import CreateOld from '@/views/pages/apply-document/CreateOld.vue'
import { CertificateDegreeList } from '@/classes/CertificateDegrees'
import OldRejected from '@/views/pages/apply-document/OldRejected.vue'
import { TypeBenefits } from '@/classes/TypeBenefits'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    OldRejected,
    BDropdownItem,
    BSpinner,
    BLink,
    CreateOld,
    BTooltip,
    BIcon,
    BDropdown,
    DownloadFile,
    LTable,
    BButton,
    Create,
    BBadge,
    ManageApplyDocInno,
  },
  data() {
    return {
      isOtm: false,
      isInno: false,
      isMe: false,
    }
  },
  computed: {
    universityList() {
      return this.$store.state.staticModule.universityList
    },
    checkMonitoringStatus() {
      return this.$store.state.staticModule.checkMonitoringAllStatus
    },
  },
  mounted() {
    if (!this.isInno) {
      this.tableColumns = this.tableColumns.filter(item => item.key !== 'monitoring')
    }
    this.loadData()
  },
  methods: {
    onCreate() {
      this.$store.commit(`${this.MODULE_NAME}/FRESH`)
    },
    getDcType(id) {
      return this.$store.getters['staticModule/getDcType'](id)
    },
    getMonitoringName(id) {
      const status = this.checkMonitoringStatus.find(item => item.id === id)
      if (status) return this.$langName(status)
      return "Noma'lum"
    },
    getEduLang(id) {
      return this.$store.getters['staticModule/getEduLangType'](id)
    },
    getApplyDocumentStatus(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatus'](id)
    },
    getApplyDocumentStatusColor(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatusColor'](id)
    },
    update(value) {
      const data = {
        id: value.id ?? null,
        dc_type: value.dc_type ?? null,
        edu_lang: value.edu_lang ?? null,
        organization: value.organization.id ?? null,
        direction: value.direction.id ?? null,
        rejected_info: value.rejected_info ?? null,
        certificate_lang: value.certificate_type?.certificate_lang?.id ?? null,
        certificate_type: value.certificate_type?.id ?? null,
        certificate_degree: value.certificate_degree ?? null,
        benefit_type: value.benefit_type ?? TypeBenefits.NO_BENEFIT_TYPE,
        disability_type: value.disability_type ?? null,
      }
      if (this.canUpdateByInno(value)) {
        data.status = value.status
        data.scientific_work_name = value.scientific_work_name
        data.applied_doc_permissions = value.applied_doc_permissions
        data.quarter = value.quarter.id
        data.monitoring_1 = value.monitoring_1
        data.monitoring_2 = value.monitoring_2
        data.monitoring_3 = value.monitoring_3
        data.course = value.course
        data.monitoring_reports = value.monitoring_reports
      }
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, JSON.parse(JSON.stringify(data)))
      this.$refs.createApplyDocument.loadDirections()
    },
    updateOld(value) {
      const data = {
        id: value.id ?? null,
        dc_type: value.dc_type ?? null,
        edu_lang: value.edu_lang ?? null,
        direction_id: value.direction.id ?? null,
        user: value.user ?? null,
        user_id: value.user?.id,
      }
      data.advisor = value.advisor
      data.advisor_id = value.advisor?.id
      data.old_apply_doc_file = value.old_apply_doc_file
      data.status = value.status
      data.scientific_work_name = value.scientific_work_name
      data.quarter_id = value.quarter.id
      data.course = value.course
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, JSON.parse(JSON.stringify(data)))
      this.$refs.createOldApplyDocument.loadData()
    },
    remove(data) {
      this.$removeSwal(this, data, `${this.MODULE_NAME}/del`, this.refetchData)
    },
    removeOld(data) {
      this.$removeSwal(this, data.id, `${this.MODULE_NAME}/oldDelete`, this.refetchData)
    },
    toggleFilter() {
      this.FILTER = !this.FILTER
    },
    refetchTable(extraParams) {
      this.$refs.LTable.extraParams = {
        ...this.$refs.LTable.extraParams,
        ...extraParams,
        is_me: this.isMe,
      }
      this.refetchData()
    },
    refetchData() {
      this.$refs.LTable.refetchData()
    },
    loadData() {
      this.$store.commit('staticModule/SET_UNIVERSITY_LIST', [])
      if (this.$route.meta.isOtm) {
        this.isOtm = this.$route.meta.isOtm
        this.$refs.LTable.extraParams = this.isMinster ? { is_minster: true } : { is_otm: this.isOtm }
        if (myOrganization()) this.$store.commit('staticModule/SET_UNIVERSITY_LIST', [myOrganization()])
      }
      if (this.$route.meta.isMe) {
        this.isMe = true
      }
      if (this.$route.meta.isInno) {
        this.isInno = this.$route.meta.isInno
        this.$refs.LTable.extraParams = { is_inno: this.isInno }
        this.$store.dispatch('staticModule/regions')
      }
      this.$store.commit(`${this.MODULE_NAME}/FRESH`)
      this.$store.dispatch('staticModule/activeQuarter')
      this.$store.dispatch('period/list', { limit: 9999, ordering: '-admission_year' })
      this.$store.dispatch('staticModule/quarters')

      if (myOrganization() && this.isOtm) {
        this.$store.dispatch('staticModule/directionList', { org_id: myOrganization().id })
      }
      this.refetchTable()
    },
    manageApplyDoc(data) {
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, data)
      this.$refs.manageApplyDocInno.loadDirections()
    },
    changeToDraft(data) {
      this.$removeSwal(
        this,
        data,
        `${this.MODULE_NAME}/changeToDraft`,
        this.refetchData,
        {
          text: '',
          confirmButtonText: 'Change To Draft',
          successTitle: 'Change To Draft',
          successText: 'Successfully completed',
          errorText: 'There was an error in successful execution',
        },
      )
    },
    confirmOld(data) {
      this.$removeSwal(this, data.id, `${this.MODULE_NAME}/oldConfirm`, () => {
        this.refetchData()
      }, {
        text: '',
        confirmButtonText: 'Yes, accept it!',
        successTitle: 'Accepted!',
        successText: 'This apply document has been accepted.',
        errorText: 'This apply document has not been accepted.',
      })
    },
    rejectedOld(data) {
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, { id: data.id })
    },
    getCertificateName(data) {
      const arr = []
      if (data.certificate_type) {
        if (data.certificate_type.certificate_lang) {
          arr.push(this.$langName(data.certificate_type.certificate_lang))
        }
        arr.push(data.certificate_type.name)
      }
      if (data.certificate_degree) {
        const find = CertificateDegreeList.find(degree => degree.id === data.certificate_degree)
        if (find) {
          arr.push(find.name)
        }
      }
      return arr.join(', ')
    },
  },
  setup() {
    const filterClass = new ApplyDocument()
    const MODULE_NAME = 'applyDocument'
    const MODULE_PERIOD = 'period'
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, applyDocumentModule)
    if (!store.hasModule(MODULE_PERIOD)) store.registerModule(MODULE_PERIOD, periodModule)
    const { staticModule } = store.state
    const userData = getUserData()
    const isMinster = userData.role_id === staticModule.roleMinistry
    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
      if (store.hasModule(MODULE_PERIOD)) store.unregisterModule(MODULE_PERIOD)
    })

    return {
      ...config(MODULE_NAME),
      MODULE_NAME,
      ...permission(),
      filterClass,
      userData,
      isMinster,
    }
  },
}
</script>
